import { AVAILABILITY_CONFIG, SERVICE_CATEGORIES, DURATION_TYPES } from "./AvailabilityConfig";

/**
 * Utility class for centralized availability calculations.
 */
class AvailabilityUtils {
  /**
   * getServiceCategory - Normalize category string to known enum
   * @param {string|object} category - Category name or object
   * @returns {string} - Service Category Enum (WORK, STAY, PLAY) or null
   */
  static getServiceCategory(category) {
    if (!category) return null;
    let catName = "";
    if (typeof category === "string") catName = category.toUpperCase();
    else if (typeof category === "object") catName = (category.name || category.code || "").toUpperCase();

    if (Object.values(SERVICE_CATEGORIES).includes(catName)) {
      return catName;
    }
    return null; // Fallback or handle unknown
  }

  /**
   * getTimeConstraints - Get default time strings for a category
   * @param {string} category - Normalized Category Name
   * @param {string} durationType - Normalized Duration Type
   * @returns {object} { startTime: "09:00", endTime: "17:00", spansOvernight: boolean }
   */
  static getTimeConstraints(category, durationType = DURATION_TYPES.HOURLY) {
    const config = AVAILABILITY_CONFIG[category];
    if (!config) {
      // Return generic fallback if category unknown
      return { startTime: "00:00", endTime: "23:59", spansOvernight: false };
    }

    const normDuration = durationType ? durationType.toUpperCase() : config.defaultDuration;

    // Check specific rules for duration
    if (config.rules && config.rules[normDuration]) {
      return config.rules[normDuration];
    }

    // Fallback to category defaults
    return {
      startTime: config.defaultStart,
      endTime: config.defaultEnd,
      spansOvernight: config.isOvernight,
    };
  }

  /**
   * getDefaultTimeRange - Calculate exact Date objects for a start date
   * @param {string} category - Service Category
   * @param {string} durationType - Duration Type
   * @param {Date} startDate - The selected start date (time part might be ignored or used as base)
   * @returns {object} { start: Date, end: Date } in local time (based on input date's zone)
   */
  static getDefaultTimeRange(category, durationType, startDate) {
    if (!startDate) return { start: null, end: null };

    const catName = this.getServiceCategory(category) || SERVICE_CATEGORIES.WORK; // Default to WORK
    const constraints = this.getTimeConstraints(catName, durationType);

    const start = new Date(startDate);
    const end = new Date(startDate);

    // Parse constraint strings "HH:MM"
    const [startH, startM] = constraints.startTime.split(":").map(Number);
    const [endH, endM] = constraints.endTime.split(":").map(Number);

    start.setHours(startH, startM, 0, 0);

    // Add overnight push if needed
    if (constraints.spansOvernight) {
      end.setDate(end.getDate() + 1);
    }

    end.setHours(endH, endM, 0, 0);

    return { start, end };
  }

  /**
   * isTimeWithinConstraints - Check if a specific time string is within allowed constraints
   * @param {string} timeStr - Time in "HH:MM" format
   * @param {string} category - Service Category
   * @param {string} durationType - Duration Type
   * @returns {boolean}
   */
  static isTimeWithinConstraints(timeStr, category, durationType) {
    if (!timeStr) return false;
    const catName = this.getServiceCategory(category) || SERVICE_CATEGORIES.WORK;
    const constraints = this.getTimeConstraints(catName, durationType);

    if (constraints.spansOvernight) return true; // Overnight spans usually allow 24h selection

    const [h, m] = timeStr.split(":").map(Number);
    const [startH, startM] = constraints.startTime.split(":").map(Number);
    const [endH, endM] = constraints.endTime.split(":").map(Number);

    const currentMinutes = h * 60 + m;
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }
}

export default AvailabilityUtils;
